import React, { useCallback, memo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { TripLodging, ViewLodging } from "@/types/accommodation";
import { format } from "date-fns";
import { Bed, Check, Info, Trash } from "lucide-react";
import { formatImageUrl, formatPrice } from "@/lib/general";
import useAccommodationStore from "@/stores/tripBuilder/useAccommodation";
import currencyStore from "@/stores/ui/currencyStore";
import logger from "@/utils/logger";
const convertTripLodgingToViewLodging = (lodging: TripLodging): ViewLodging => {
  return {
    id: lodging.id,
    name: lodging.name,
    type: lodging.type,
    mainImageURL: lodging.mainImageURL,
    latitude: lodging.coordinates.latitude,
    longitude: lodging.coordinates.longitude,
    description: lodging.description,
    moreInfo: {
      reviewCount: lodging.moreInfo?.reviewCount ?? 0,
      reviewScore: lodging.moreInfo?.reviewScore ?? 0,
    },
  };
};

interface LodgingsSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  lodgings: TripLodging[];
  checkInDate?: string;
  checkOutDate?: string;
  mainLodging: TripLodging | null;
  setViewLodging: (lodging: ViewLodging) => void;
  isAdmin: boolean;
}

const LodgingsSidebar: React.FC<LodgingsSidebarProps> = memo(
  ({
    isOpen,
    onClose,
    lodgings,
    checkInDate,
    checkOutDate,
    mainLodging,
    setViewLodging,
    isAdmin,
  }) => {
    const {
      setMainLodging,
      setIsMainLodgingModalOpen,
      setViewingLodging,
      removeLodging,
      setCoreLodging,
    } = useAccommodationStore();
    const { selectedCurrency } = currencyStore();
    const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);

    const checkIsAdmin = useCallback(() => isAdmin, [isAdmin]);

    const handleSetMainLodging = useCallback(
      (lodging: TripLodging) => {
        if (checkIsAdmin()) {
          setMainLodging(lodging);
        } else {
          setPermissionDialogOpen(true);
        }
      },
      [setMainLodging, checkIsAdmin, setPermissionDialogOpen]
    );

    const handleViewLodging = useCallback(
      (lodging: TripLodging) => {
        logger.log("lodging", lodging);
        const viewLodging = convertTripLodgingToViewLodging(lodging);
        setViewingLodging(viewLodging);
        setIsMainLodgingModalOpen(true);
        setViewLodging(viewLodging);
        setCoreLodging(null);
        onClose();
      },
      [
        setViewingLodging,
        setIsMainLodgingModalOpen,
        setViewLodging,
        onClose,
        setCoreLodging,
      ]
    );

    const handleRemoveLodging = useCallback(
      (e: React.MouseEvent, hotelId: number) => {
        e.stopPropagation();
        if (checkIsAdmin()) {
          removeLodging(hotelId);
        } else {
          setPermissionDialogOpen(true);
        }
      },
      [removeLodging, setPermissionDialogOpen, checkIsAdmin]
    );

    // Filter lodgings to only show the main lodging for non-admin users
    const displayLodgings = isAdmin
      ? lodgings
      : mainLodging
      ? [mainLodging]
      : [];

    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute right-4 top-[3.5rem] h-[calc(100%-4.1rem)] w-[calc(100%-23rem)] bg-white/60 backdrop-blur-sm shadow-lg rounded-lg overflow-hidden z-[1001]"
          >
            <div className="p-2 h-full flex flex-col">
              {displayLodgings.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center bg-white border border-neutral-100 rounded-lg p-4 text-neutral-500 text-sm">
                  <Bed className="w-8 h-8 text-neutral-300 mb-4" />
                  <div className="text-center space-y-2">
                    <h2 className="text-sm font-medium text-neutral-800">
                      No stay added yet
                    </h2>
                    <p className="text-sm font-light text-neutral-500">
                      Select a hotel from the list or map
                    </p>
                  </div>
                </div>
              ) : (
                <motion.div className="flex-1 overflow-y-auto space-y-3 no-scrollbar rounded-lg">
                  <AnimatePresence mode="popLayout">
                    {displayLodgings.map((lodging, index) => (
                      <motion.div
                        key={lodging.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{
                          opacity: 0,
                          scale: 0.95,
                          transition: {
                            duration: 0.2,
                          },
                        }}
                        layout
                        className={`p-2 bg-white rounded-lg shadow-sm hover:shadow-md hover:shadow-indigo-100 transition-all relative duration-300 origin-top group ${
                          mainLodging?.id === lodging.id
                            ? "border border-indigo-300"
                            : "border border-neutral-100"
                        }`}
                      >
                        <div className="flex gap-3">
                          <div className="w-28 h-28 flex-shrink-0 rounded-md overflow-hidden">
                            <img
                              src={formatImageUrl(lodging.mainImageURL, "low")}
                              alt={lodging.name}
                              className="w-full h-full object-cover"
                            />
                          </div>

                          <div className="flex-1 min-w-0">
                            <h3 className="font-medium text-sm mb-1 truncate">
                              {lodging.name}
                            </h3>
                            {checkInDate && checkOutDate && (
                              <div className="text-xs font-light text-neutral-500 mb-2">
                                <span>
                                  {format(new Date(checkInDate), "MMM d")} -{" "}
                                  {format(new Date(checkOutDate), "MMM d")}
                                </span>
                              </div>
                            )}
                            <div className="text-sm font-medium">
                              {formatPrice(
                                selectedCurrency.code,
                                lodging.pricing.amount
                              )}
                              <span className="text-xs font-light text-neutral-500 ml-1">
                                per night
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="absolute bottom-2 left-[8.2rem] flex space-x-2 items-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSetMainLodging(lodging);
                            }}
                            className={`py-1.5 rounded-full duration-300 transition-all flex items-center ${
                              mainLodging?.id === lodging.id
                                ? "text-indigo-600"
                                : "text-neutral-500 hover:text-indigo-500"
                            }`}
                          >
                            {mainLodging?.id === lodging.id ? (
                              <>
                                <span className="text-xs font-medium">
                                  Selected
                                </span>
                                <Check className="w-3.5 h-3.5 ml-1.5" />
                              </>
                            ) : (
                              <span className="text-xs font-medium">
                                Select
                              </span>
                            )}
                          </button>
                        </div>
                        <div className="absolute bottom-2 right-2 flex space-x-2 items-center">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewLodging(lodging);
                            }}
                            className="px-1.5 py-1.5 rounded-full text-neutral-500 hover:text-neutral-600 hover:bg-neutral-100 duration-300 transition-all"
                          >
                            <Info className="w-3.5 h-3.5" />
                          </button>
                          {isAdmin && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRemoveLodging(e, lodging.id);
                              }}
                              className="px-1.5 py-1.5 rounded-full text-neutral-500 hover:text-red-500 hover:bg-red-50 duration-300 transition-all"
                            >
                              <Trash className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.isOpen === nextProps.isOpen &&
      prevProps.checkInDate === nextProps.checkInDate &&
      prevProps.checkOutDate === nextProps.checkOutDate &&
      prevProps.lodgings === nextProps.lodgings &&
      JSON.stringify(prevProps.mainLodging) ===
        JSON.stringify(nextProps.mainLodging)
    );
  }
);

LodgingsSidebar.displayName = "LodgingsSidebar";

export default LodgingsSidebar;
